import { useState, useCallback } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { InlineTextField } from "@/components/inline-edit/inline-text-field";
import { InlineSelectField } from "@/components/inline-edit/inline-select-field";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { Facility, LenderProduct, LenderPricingTier } from "@shared/schema";
import { PricingScheduleSection, type PricingScheduleFormData } from "@/components/facilities/pricing-schedule-section";
import { FeesSection, type FeeFormData } from "@/components/facilities/fees-section";

const REPAYMENT_FREQUENCY_OPTIONS = [
  { value: "weekly", label: "Weekly" },
  { value: "fortnightly", label: "Fortnightly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "annually", label: "Annually" },
];

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatPercentage(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return `${(num * 100).toFixed(2)}%`;
}

interface FacilityFinancialTabProps {
  facility: Facility;
  dealId: string;
}

export function FacilityFinancialTab({ facility, dealId }: FacilityFinancialTabProps) {
  const { toast } = useToast();
  const [isAutoPopulating, setIsAutoPopulating] = useState(false);

  // F018: Pricing schedules from API (inline edit mode)
  const { data: pricingSchedules = [] } = useQuery<PricingScheduleFormData[]>({
    queryKey: ["/api/crm/facilities", facility.id, "pricing-schedules"],
    enabled: !!facility.id,
  });

  // F018: Fees from API
  const { data: facilityFees = [] } = useQuery<FeeFormData[]>({
    queryKey: ["/api/crm/facilities", facility.id, "fees"],
    enabled: !!facility.id,
  });

  // Fetch lender products based on facility.lenderId
  const { data: lenderProducts = [] } = useQuery<LenderProduct[]>({
    queryKey: [`/api/crm/lenders/${facility.lenderId}/products`],
    enabled: !!facility.lenderId,
  });

  // Fetch selected product details (including pricing tiers)
  const { data: selectedProduct } = useQuery<LenderProduct>({
    queryKey: [`/api/crm/lender-products/${facility.lenderProductId}`],
    enabled: !!facility.lenderProductId,
  });

  // Fetch pricing tiers for selected product
  const { data: _pricingTiers = [] } = useQuery<LenderPricingTier[]>({
    queryKey: [`/api/crm/lender-products/${facility.lenderProductId}/pricing-tiers`],
    enabled: !!facility.lenderProductId,
  });

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Facility>) => {
      const response = await apiRequest("PATCH", `/api/crm/facilities/${facility.id}`, updates);
      return response;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["/api/crm/facilities", facility.id] });
      const previousFacility = queryClient.getQueryData(["/api/crm/facilities", facility.id]);
      if (previousFacility) {
        queryClient.setQueryData(["/api/crm/facilities", facility.id], {
          ...previousFacility,
          ...updates,
        });
      }
      return { previousFacility };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousFacility) {
        queryClient.setQueryData(["/api/crm/facilities", facility.id], context.previousFacility);
      }
      toast({
        title: "Error",
        description: error.message || "Failed to save changes",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities", facility.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", dealId, "facilities"] });
    },
  });

  const handleFieldUpdate = async (field: keyof Facility, value: any): Promise<void> => {
    await updateMutation.mutateAsync({ [field]: value });
  };

  // Auto-populate fields when product is selected
  const handleProductSelect = async (productId: string | null) => {
    if (!productId || productId === "none") {
      await handleFieldUpdate("lenderProductId", null);
      return;
    }

    setIsAutoPopulating(true);

    try {
      const product = await apiRequest("GET", `/api/crm/lender-products/${productId}`) as unknown as LenderProduct;
      const tiers = await apiRequest("GET", `/api/crm/lender-products/${productId}/pricing-tiers`) as unknown as LenderPricingTier[];

      let selectedMargin = null;
      if (tiers.length === 1) {
        selectedMargin = tiers[0].margin;
      }

      const updates: Partial<Facility> = {
        lenderProductId: productId,
        baseRate: product.currentBaseRate,
        margin: selectedMargin || null,
        termMonths: product.maxTermMonths || null,
        ioMonths: null,
        repaymentFrequency: "monthly",
      };

      await updateMutation.mutateAsync(updates);

      toast({
        title: "Product Applied",
        description: `Financial terms auto-populated from ${product.productName}. You can now override any field.`,
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to apply product defaults",
        variant: "destructive",
      });
    } finally {
      setIsAutoPopulating(false);
    }
  };

  const productOptions = lenderProducts.map(product => ({
    value: product.id,
    label: `${product.productName} (${product.productCode})`,
  }));

  // F018: Handle pricing schedule changes via individual POST/PATCH/DELETE
  const handlePricingSchedulesChange = useCallback(
    async (updated: PricingScheduleFormData[]) => {
      try {
        const current = pricingSchedules;
        const currentIds = new Set(current.filter((s) => s.id).map((s) => s.id!));
        const updatedIds = new Set(updated.filter((s) => s.id).map((s) => s.id!));

        // DELETE: items in current but not in updated
        const toDelete = current.filter((s) => s.id && !updatedIds.has(s.id));
        for (const item of toDelete) {
          await apiRequest("DELETE", `/api/crm/facilities/${facility.id}/pricing-schedules/${item.id}`);
        }

        // POST: items without id (new)
        for (const item of updated) {
          if (!item.id) {
            const { id: _, ...payload } = item;
            await apiRequest("POST", `/api/crm/facilities/${facility.id}/pricing-schedules`, payload);
          }
        }

        // PATCH: items with id that exist in current (updated)
        for (const item of updated) {
          if (item.id && currentIds.has(item.id)) {
            const { id: _, ...payload } = item;
            await apiRequest("PATCH", `/api/crm/facilities/${facility.id}/pricing-schedules/${item.id}`, payload);
          }
        }

        queryClient.invalidateQueries({
          queryKey: ["/api/crm/facilities", facility.id, "pricing-schedules"],
        });
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "Failed to save pricing schedules",
          variant: "destructive",
        });
      }
    },
    [facility.id, pricingSchedules, toast],
  );

  // F018: Handle fees changes via individual POST/PATCH/DELETE
  const handleFeesChange = useCallback(
    async (updated: FeeFormData[]) => {
      try {
        const current = facilityFees;
        const currentIds = new Set(current.filter((f) => f.id).map((f) => f.id!));
        const updatedIds = new Set(updated.filter((f) => f.id).map((f) => f.id!));

        // DELETE: items in current but not in updated
        const toDelete = current.filter((f) => f.id && !updatedIds.has(f.id));
        for (const item of toDelete) {
          await apiRequest("DELETE", `/api/crm/facilities/${facility.id}/fees/${item.id}`);
        }

        // POST: items without id (new)
        for (const item of updated) {
          if (!item.id) {
            const { id: _, ...payload } = item;
            await apiRequest("POST", `/api/crm/facilities/${facility.id}/fees`, payload);
          }
        }

        // PATCH: items with id that exist in current (updated)
        for (const item of updated) {
          if (item.id && currentIds.has(item.id)) {
            const { id: _, ...payload } = item;
            await apiRequest("PATCH", `/api/crm/facilities/${facility.id}/fees/${item.id}`, payload);
          }
        }

        queryClient.invalidateQueries({
          queryKey: ["/api/crm/facilities", facility.id, "fees"],
        });
      } catch (error: any) {
        toast({
          title: "Error",
          description: error.message || "Failed to save fees",
          variant: "destructive",
        });
      }
    },
    [facility.id, facilityFees, toast],
  );

  return (
    <div className="space-y-3">
      {/* Section 1: Lender Product Selection */}
      {facility.lenderId && (
        <Card>
          <CardContent className="pt-3">
            <h3 className="text-xs font-semibold text-muted-foreground mb-2">LENDER PRODUCT</h3>
            <InlineSelectField
              value={facility.lenderProductId || "none"}
              options={[
                { value: "none", label: "Select product (optional)" },
                ...productOptions,
              ]}
              onSave={handleProductSelect}
              label="Product"
              placeholder="Select a lender product"
              testId="select-lender-product"
              disabled={isAutoPopulating}
            />
            {facility.lenderProductId && selectedProduct && (
              <p className="text-xs text-muted-foreground mt-1">
                {selectedProduct.productName} - Base Rate: {formatPercentage(selectedProduct.currentBaseRate)}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Section 2: Pricing Schedule (F018 — replaces Interest Rates card) */}
      <Card data-testid="financial-pricing-schedule">
        <CardContent className="pt-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">PRICING SCHEDULE</h3>
          <PricingScheduleSection
            schedules={pricingSchedules.length > 0 ? pricingSchedules : [{
              sequence: 1,
              paymentType: "interest_only",
              rateType: "fixed",
              entryMode: "composed",
              baseRate: facility.baseRate ? String(parseFloat(facility.baseRate) * 100) : null,
              margin: facility.margin ? String(parseFloat(facility.margin) * 100) : null,
              totalRate: facility.totalRate ? String(parseFloat(facility.totalRate) * 100) : null,
              effectiveFrom: "",
              effectiveTo: null,
              regimeMonths: facility.termMonths ?? null,
              repaymentFrequency: facility.repaymentFrequency || "monthly",
              repaymentOverride: facility.repaymentOverride || null,
              amortisationPeriodMonths: facility.amortisationPeriodMonths ?? null,
              source: facility.lenderProductId ? "product_snapshot" : "manual",
              notes: null,
            }]}
            onChange={handlePricingSchedulesChange}
            mode="inline"
          />
        </CardContent>
      </Card>

      {/* Section 3: Term & Repayment (legacy scalar fields — inline edit) */}
      <Card>
        <CardContent className="pt-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">TERM & REPAYMENT</h3>
          <div className="grid grid-cols-3 gap-3">
            <InlineTextField
              value={facility.termMonths?.toString() || ""}
              onSave={(value) => handleFieldUpdate("termMonths", value ? parseInt(value) : null)}
              label="Term (Months)"
              placeholder="e.g., 60"
              testId="input-term-months"
            />
            <InlineTextField
              value={facility.ioMonths?.toString() || ""}
              onSave={(value) => handleFieldUpdate("ioMonths", value ? parseInt(value) : null)}
              label="I/O Period (Months)"
              placeholder="e.g., 12"
              testId="input-io-months"
            />
            <InlineSelectField
              value={facility.repaymentFrequency || "monthly"}
              options={REPAYMENT_FREQUENCY_OPTIONS}
              onSave={(value) => handleFieldUpdate("repaymentFrequency", value)}
              label="Repayment Frequency"
              placeholder="Select frequency"
              testId="select-repayment-frequency"
            />
          </div>
          <div className="grid grid-cols-3 gap-3 mt-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">I/O Repayment</p>
              <p className="text-sm font-medium text-blue-600">
                {formatCurrency(facility.ioRepayment)}
              </p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">P&I Repayment</p>
              <p className="text-sm font-medium text-blue-600">
                {formatCurrency(facility.piRepayment)}
              </p>
            </div>
            <InlineTextField
              value={facility.repaymentOverride || ""}
              onSave={(value) => handleFieldUpdate("repaymentOverride", value || null)}
              label="Repayment Override"
              placeholder="Manual override"
              testId="input-repayment-override"
            />
          </div>
        </CardContent>
      </Card>

      {/* Section 4: Fees (F018 — new section) */}
      <Card data-testid="financial-fees-section">
        <CardContent className="pt-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">FEES</h3>
          <FeesSection
            fees={facilityFees}
            onChange={handleFeesChange}
          />
        </CardContent>
      </Card>

      {/* Section 5: Commission */}
      <Card>
        <CardContent className="pt-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">COMMISSION</h3>
          <div className="grid grid-cols-2 gap-3">
            <InlineTextField
              value={facility.customCommissionRate ? String(parseFloat(facility.customCommissionRate) * 100) : ""}
              onSave={(value) => handleFieldUpdate("customCommissionRate", value ? String(parseFloat(value) / 100) : null)}
              label="Custom Commission Rate (%)"
              placeholder="e.g., 1.25"
              testId="input-custom-commission-rate"
            />
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Effective Rate</p>
              <p className="text-sm font-medium">
                {facility.customCommissionRate
                  ? formatPercentage(facility.customCommissionRate)
                  : "Default from lender"}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* F018: LOAN AMOUNTS card removed — Overview tab owns amounts */}
    </div>
  );
}
